import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Trash2, Link as LinkIcon, Shield, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";


export default function AdminDashboard() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);

    // Invite Form State
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('user');
    const [inviteLoading, setInviteLoading] = useState(false);

    // Dev Helper: Last generated link
    const [generatedLink, setGeneratedLink] = useState(null);

    useEffect(() => {
        fetchUsers();
        // Poll every 5 seconds to keep status updated
        const interval = setInterval(fetchUsers, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (err) {
            console.error("Failed to fetch users", err);
            toast.error("Failed to load user list");
        } finally {
            setLoading(false);
        }
    };

    const handleResendInvite = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE}/admin/invite/resend/${userId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setGeneratedLink(res.data.debug_link);
            setShowInviteModal(true);
            toast.success("New invite link generated!");
        } catch (err) {
            console.error("Resend failed", err);
            toast.error("Failed to resend invite");
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        setGeneratedLink(null);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE}/admin/invite`,
                { email: inviteEmail, role: inviteRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("Invite sent successfully!");
            setGeneratedLink(res.data.debug_link); // Show link for now since no email service
            setInviteEmail('');
            fetchUsers(); // Refresh list to show new pending user
        } catch (err) {
            console.error("Invite failed", err);
            toast.error(err.response?.data?.detail || "Failed to invite user");
        } finally {
            setInviteLoading(false);
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE}/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("User deleted");
            setUsers(prev => prev.filter(u => u._id !== userId));
        } catch (err) {
            toast.error("Failed to delete user");
        }
    };

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-6 border-b border-slate-200 dark:border-slate-800">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
                        <Shield className="w-8 h-8 text-brand-600" />
                        Admin Compliance Portal
                    </h1>
                    <p className="text-slate-500 mt-1">Manage user access and roles.</p>
                </div>

                <button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors shadow-sm"
                >
                    <UserPlus className="w-4 h-4" />
                    Invite User
                </button>
            </div>

            {/* User List Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider">
                                <th className="p-4 font-medium">User</th>
                                <th className="p-4 font-medium">Role</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Invite</th>
                                <th className="p-4 font-medium">Joined</th>
                                <th className="p-4 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
                            {users.map(user => (
                                <tr key={user._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center text-brand-600 font-bold text-xs">
                                                {user.email.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="font-medium text-slate-800 dark:text-slate-200">{user.email}</div>
                                                {user.name && <div className="text-xs text-slate-500">{user.name}</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${user.role === 'admin'
                                            ? 'bg-purple-50 text-purple-600 border-purple-100 dark:bg-purple-900/30 dark:border-purple-800'
                                            : 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/30 dark:border-blue-800'
                                            }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        {user.is_active ? (
                                            <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                                                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                Active
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
                                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                Pending Invite
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        {!user.is_active && (
                                            <button
                                                onClick={() => handleResendInvite(user._id)}
                                                className="text-xs bg-brand-50 hover:bg-brand-100 text-brand-600 px-2 py-1 rounded border border-brand-200 transition-colors"
                                                title="Generate new invite link"
                                            >
                                                Resend Link
                                            </button>
                                        )}
                                    </td>
                                    <td className="p-4 text-slate-500">
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => handleDelete(user._id)}
                                            className="p-2 hover:bg-red-50 hover:text-red-600 rounded-lg text-slate-400 transition-colors"
                                            title="Delete User"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && users.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-slate-500">
                                        No users found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Invite Modal */}
            <AnimatePresence>
                {showInviteModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Invite New User</h3>
                                <button
                                    onClick={() => setShowInviteModal(false)}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-6">
                                {generatedLink ? (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
                                        <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
                                            <CheckCircle className="w-5 h-5" />
                                            Invite Sent!
                                        </div>
                                        <p className="text-sm text-slate-600 dark:text-slate-300">
                                            Since email service is mocked, verify using this link:
                                        </p>
                                        <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 text-xs font-mono break-all relative">
                                            {generatedLink}
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(generatedLink);
                                                    toast.success("Copied!");
                                                }}
                                                className="absolute right-1 top-1 p-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded"
                                            >
                                                <LinkIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setGeneratedLink(null);
                                                setShowInviteModal(false);
                                            }}
                                            className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded"
                                        >
                                            Done
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleInvite} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                                Email Address
                                            </label>
                                            <input
                                                type="email"
                                                value={inviteEmail}
                                                onChange={(e) => setInviteEmail(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                                placeholder="colleague@company.com"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                                Role
                                            </label>
                                            <select
                                                value={inviteRole}
                                                onChange={(e) => setInviteRole(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                            >
                                                <option value="user">User (Recruiter)</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                        </div>

                                        <div className="pt-2">
                                            <button
                                                type="submit"
                                                disabled={inviteLoading}
                                                className="w-full bg-brand-600 hover:bg-brand-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2"
                                            >
                                                {inviteLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                                                Send Invite Link
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
